import { useMemo } from 'react';
import type { I18n, MessageDescriptor } from '@lingui/core';
import { useLingui } from '@lingui/react';

import usePageContext from '@rover/react-lib/src/context/usePageContext';
import {
  DEFAULT_HEADLINE_TEXT,
  DEFAULT_SUBLINE_TEXT,
} from '@rover/react-lib/src/pages/index/HomePage/HomePage.constants';
import { selectors as homePageSelectors } from '@rover/react-lib/src/pages/index/HomePage/HomePage.duck';
import { useApiFrontendHomepageConfigurationRetrieve } from '@rover/rsdk/src/apiClient/latest/api-frontend-homepage-configuration-retrieve';
import type { HomepageConfiguration as ApiHomepageConfiguration } from '@rover/rsdk/src/apiClient/latest/model/homepageConfiguration';
import type {
  HomepageConfiguration as ReduxHomepageConfiguration,
  HomepageMetaTag,
  HomepageMetaTags,
} from '@rover/types/src/HomepageConfiguration';

import useDucks from './useDucks';

export type DefaultHomepageConfigurationSelectors = Omit<
  ReduxHomepageConfiguration,
  HomepageMetaTag
> & {
  metaTags: HomepageMetaTags;
};

export type ShimmedHomepageConfigurationSelectors = Omit<
  ApiHomepageConfiguration,
  HomepageMetaTag
> & {
  metaTags: HomepageMetaTags;
};

const selector = (state): DefaultHomepageConfigurationSelectors => ({
  metaTags: homePageSelectors.getMetaTags(state),
  headlineText: homePageSelectors.getHeadlineText(state),
  subHeadlineText: homePageSelectors.getSubheadlineText(state),
  locationName: homePageSelectors.getLocationName(state),
  hasActivePets: homePageSelectors.getHasActivePets(state),
});

// transforms the raw api data to match the current duck pattern
const transformReactQueryData = (
  reactQueryData: ApiHomepageConfiguration | undefined
): DefaultHomepageConfigurationSelectors => {
  const { title, metaDescription, ...data } = reactQueryData ?? {
    headlineText: DEFAULT_HEADLINE_TEXT,
    subHeadlineText: DEFAULT_SUBLINE_TEXT,
    locationName: null,
    hasActivePets: false,
    title: '',
    metaDescription: '',
  };

  const metaTags = { title, metaDescription };

  return { ...data, metaTags };
};

// headlineText/subHeadlineText only type as descriptors because of the default copy; the
// API returns strings (subHeadlineText may be null). Resolve here so consumers get plain
// strings. subHeadlineText stays nullable — its presence drives surrounding styling.
const resolveText = (value: string | MessageDescriptor, i18n: I18n): string =>
  typeof value === 'string' ? value : i18n._(value);

const useHomePageConfigurationShim = (): ShimmedHomepageConfigurationSelectors => {
  const { i18n } = useLingui();
  const { isReactQueryRoute } = usePageContext();
  const { data: selectedData } = useDucks({ selector });
  const { data: reactQueryData } = useApiFrontendHomepageConfigurationRetrieve({
    query: { enabled: isReactQueryRoute },
  });

  return useMemo(() => {
    const { headlineText, subHeadlineText, ...data } = isReactQueryRoute
      ? transformReactQueryData(reactQueryData)
      : selectedData;

    return {
      ...data,
      headlineText: resolveText(headlineText, i18n),
      subHeadlineText: subHeadlineText == null ? null : resolveText(subHeadlineText, i18n),
    };
  }, [isReactQueryRoute, reactQueryData, selectedData, i18n]);
};

export default useHomePageConfigurationShim;
